import { Download, Mail } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { download } from '../../api/client'
import { useEventSends, useInvitations, useMe, useTeams } from '../../api/hooks'
import type { Event, SendFilter } from '../../api/types'
import { Badge, Button, Card, EmptyState, FilterChips, Input, LiveDot, Select, TableSkeleton } from '../../components/ui'
import { CUSTOMER_TYPE_LABEL, fmtDateTime, SEND_STATUS_LABEL } from '../../lib/format'

const FILTERS: { key: SendFilter; label: string }[] = [
  { key: 'all', label: 'Alla' },
  { key: 'opened', label: 'Öppnade' },
  { key: 'clicked', label: 'Klickade' },
  { key: 'registered', label: 'Anmälda via länk' },
  { key: 'unopened', label: 'Ej öppnade' },
  { key: 'failed', label: 'Misslyckade' },
]

/** Every recipient across the event's invitations – the rows behind "skickade",
 *  "öppnade" and "klickade". Filters live in the URL so numbers elsewhere can deep-link here. */
export function SendsTab({ event: e }: { event: Event }) {
  const [params, setParams] = useSearchParams()
  const me = useMe()
  const isManager = me.data?.role !== 'RADGIVARE'
  const filter = (params.get('filter') as SendFilter) || 'all'
  const teamId = params.get('team_id') ?? ''
  const invitationId = params.get('invitation_id') ?? ''
  const [q, setQ] = useState('')
  const sends = useEventSends(e.id, { filter, team_id: teamId || undefined, invitation_id: invitationId || undefined })
  const teams = useTeams()
  const invs = useInvitations(e.id, isManager)

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v && v !== 'all') next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase()
    return (sends.data ?? []).filter((r) => !s || `${r.customer_name} ${r.to_email} ${r.category_name}`.toLowerCase().includes(s))
  }, [sends.data, q])

  const hasFilter = filter !== 'all' || teamId || invitationId

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">Alla mottagare av eventets inbjudningar. Klicka på en siffra någon annanstans i appen och du landar här med rätt filter.</p>
        <div className="flex items-center gap-4">
          <LiveDot />
          <Button size="sm" icon={<Download className="size-4" />} onClick={() => download(`/api/events/${e.id}/export.xlsx`, `${e.name}.xlsx`)}>Exportera Excel</Button>
        </div>
      </div>

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 border-b border-line-100 px-4 py-3">
          <FilterChips options={FILTERS} value={filter} onChange={(k) => set('filter', k)} />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {isManager && teams.data && teams.data.length > 1 && (
              <Select value={teamId} onChange={(ev) => set('team_id', ev.target.value)} className="h-8 w-48 text-xs">
                <option value="">Alla team</option>
                {teams.data.map((t) => <option key={t.id} value={t.id}>{t.code} {t.name}</option>)}
              </Select>
            )}
            {isManager && invs.data && invs.data.length > 1 && (
              <Select value={invitationId} onChange={(ev) => set('invitation_id', ev.target.value)} className="h-8 w-48 text-xs">
                <option value="">Alla inbjudningar</option>
                {invs.data.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
              </Select>
            )}
            <Input placeholder="Sök mottagare…" value={q} onChange={(ev) => setQ(ev.target.value)} className="h-8 w-48 text-xs" />
            {hasFilter && <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>Rensa filter</Button>}
          </div>
        </div>
        {sends.isLoading ? <TableSkeleton rows={8} cols={7} /> : rows.length === 0 ? (
          <div className="p-6"><EmptyState icon={<Mail className="size-6" />} title={hasFilter ? 'Inga mottagare matchar filtret' : 'Inga utskick ännu'} description={hasFilter ? 'Prova ett annat filter.' : 'Skicka en inbjudan så dyker mottagarna upp här.'} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="table-head"><tr><th>Mottagare</th><th>Team</th><th>Typ</th><th>Inbjudan</th><th>Status</th><th>Skickat</th><th>Öppnat</th><th>Klickat</th><th>Anmäld</th></tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="table-row">
                    <td><div className="font-medium text-ink-900">{s.customer_name}</div><div className="text-xs text-ink-500">{s.to_email}</div></td>
                    <td className="font-mono text-xs">{s.team_code}</td>
                    <td>{s.customer_type ? <Badge tone={s.customer_type === 'KUND' ? 'success' : 'copper'}>{CUSTOMER_TYPE_LABEL[s.customer_type]}</Badge> : '–'}</td>
                    <td className="text-ink-500">{s.invitation_title}</td>
                    <td><Badge tone={s.status === 'SKICKAT' ? 'success' : s.status === 'KO' ? 'neutral' : 'danger'} dot>{SEND_STATUS_LABEL[s.status]}</Badge></td>
                    <td className="text-xs tabular-nums text-ink-500">{s.sent_at ? fmtDateTime(s.sent_at) : '–'}</td>
                    <td className="text-xs tabular-nums text-ink-500">{s.opened_at ? fmtDateTime(s.opened_at) : '–'}</td>
                    <td className="text-xs tabular-nums text-ink-500">{s.clicked_at ? fmtDateTime(s.clicked_at) : '–'}</td>
                    <td>{s.registered_at ? <Badge tone="navy">Ja</Badge> : <span className="text-xs text-ink-400">–</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-line-100 px-4 py-2 text-xs text-ink-500">{rows.length} mottagare</div>
          </div>
        )}
      </Card>
    </div>
  )
}
